import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  CreateRegionInput,
  CreateServiceAreaInput,
  PatchServiceAreaInput,
  RegionAdminView,
  ServiceAreaAdminView,
  serviceAreasAdminService,
} from '../services/serviceAreasService';

// FEAT-063 admin surface (MVP-18.6, MVP-18.25).
// Two-tab layout: "Service Areas" (zip management) and "Regions" (grouping).

// ─── Types ─────────────────────────────────────────────────────────────────

interface ServiceAreaDialogState {
  open: boolean;
  mode: 'create' | 'edit';
  id: string | null;
}

interface RegionDialogState {
  open: boolean;
  mode: 'create' | 'edit';
  id: string | null;
}

interface AssignDialogState {
  open: boolean;
  regionId: string | null;
  regionName: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const EMPTY_SA_DRAFT: CreateServiceAreaInput = {
  zip: '',
  city: '',
  state: '',
  active: true,
  regionId: null,
};

const EMPTY_REGION_DRAFT: CreateRegionInput = {
  name: '',
  active: true,
};

const ZIP_PATTERN = /^\d{5}$/;
const STATE_PATTERN = /^[A-Z]{2}$/;

// ─── Component ──────────────────────────────────────────────────────────────

export const ServiceAreasPage: React.FC = () => {
  const [tab, setTab] = React.useState(0);

  // Data
  const [serviceAreas, setServiceAreas] = React.useState<ServiceAreaAdminView[]>([]);
  const [regions, setRegions] = React.useState<RegionAdminView[]>([]);

  // UI state
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  // Pagination
  const [saPage, setSaPage] = React.useState(0);
  const [saRowsPerPage, setSaRowsPerPage] = React.useState(25);
  const [regPage, setRegPage] = React.useState(0);
  const [regRowsPerPage, setRegRowsPerPage] = React.useState(10);

  // Filter
  const [filterActive, setFilterActive] = React.useState<'all' | 'active' | 'inactive'>('all');
  const [filterRegionId, setFilterRegionId] = React.useState<string>('');

  // Service area dialog
  const [saDialog, setSaDialog] = React.useState<ServiceAreaDialogState>({
    open: false,
    mode: 'create',
    id: null,
  });
  const [saDraft, setSaDraft] = React.useState<CreateServiceAreaInput>({ ...EMPTY_SA_DRAFT });

  // Region dialog
  const [regDialog, setRegDialog] = React.useState<RegionDialogState>({
    open: false,
    mode: 'create',
    id: null,
  });
  const [regDraft, setRegDraft] = React.useState<CreateRegionInput>({ ...EMPTY_REGION_DRAFT });

  // Assign zips to region dialog
  const [assignDialog, setAssignDialog] = React.useState<AssignDialogState>({
    open: false,
    regionId: null,
    regionName: '',
  });
  const [selectedZipIds, setSelectedZipIds] = React.useState<string[]>([]);

  // ─── Data loading ──────────────────────────────────────────────────────────

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [areas, regs] = await Promise.all([
        serviceAreasAdminService.listServiceAreas(),
        serviceAreasAdminService.listRegions(),
      ]);
      setServiceAreas(areas);
      setRegions(regs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load service areas');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // ─── Filtered list ─────────────────────────────────────────────────────────

  const filteredAreas = React.useMemo(() => {
    return serviceAreas.filter((sa) => {
      if (filterActive === 'active' && !sa.active) return false;
      if (filterActive === 'inactive' && sa.active) return false;
      if (filterRegionId && sa.regionId !== filterRegionId) return false;
      return true;
    });
  }, [serviceAreas, filterActive, filterRegionId]);

  const visibleAreas = filteredAreas.slice(saPage * saRowsPerPage, saPage * saRowsPerPage + saRowsPerPage);
  const visibleRegions = regions.slice(regPage * regRowsPerPage, regPage * regRowsPerPage + regRowsPerPage);

  // ─── Service area handlers ─────────────────────────────────────────────────

  const openSaCreate = () => {
    setSaDraft({ ...EMPTY_SA_DRAFT });
    setSaDialog({ open: true, mode: 'create', id: null });
  };

  const openSaEdit = (sa: ServiceAreaAdminView) => {
    setSaDraft({
      zip: sa.zip,
      city: sa.city ?? '',
      state: sa.state ?? '',
      active: sa.active,
      regionId: sa.regionId ?? null,
    });
    setSaDialog({ open: true, mode: 'edit', id: sa.id });
  };

  const closeSaDialog = () => {
    setSaDialog({ open: false, mode: 'create', id: null });
    setSaving(false);
  };

  const handleSaSave = async () => {
    const zip = saDraft.zip.trim();
    if (!ZIP_PATTERN.test(zip)) {
      setError('ZIP code must be exactly 5 digits.');
      return;
    }
    const state = saDraft.state?.trim().toUpperCase() ?? '';
    if (state && !STATE_PATTERN.test(state)) {
      setError('State must be a 2-letter code (e.g. CA).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (saDialog.mode === 'create') {
        await serviceAreasAdminService.createServiceArea({
          zip,
          city: saDraft.city?.trim() || undefined,
          state: state || undefined,
          active: saDraft.active ?? true,
          regionId: saDraft.regionId || null,
        });
        setSuccess(`Added ZIP ${zip}.`);
      } else if (saDialog.id) {
        const patch: PatchServiceAreaInput = {
          city: saDraft.city?.trim() || undefined,
          state: state || undefined,
          active: saDraft.active,
          regionId: saDraft.regionId || null,
        };
        await serviceAreasAdminService.patchServiceArea(saDialog.id, patch);
        setSuccess(`Updated ZIP ${zip}.`);
      }
      closeSaDialog();
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save service area');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (sa: ServiceAreaAdminView) => {
    setError(null);
    try {
      await serviceAreasAdminService.patchServiceArea(sa.id, { active: !sa.active });
      setSuccess(`ZIP ${sa.zip} is now ${sa.active ? 'inactive' : 'active'}.`);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle service area');
    }
  };

  const handleSaDelete = async (sa: ServiceAreaAdminView) => {
    if (!window.confirm(`Delete ZIP ${sa.zip}? This cannot be undone.`)) return;
    setError(null);
    try {
      await serviceAreasAdminService.deleteServiceArea(sa.id);
      setSuccess(`Deleted ZIP ${sa.zip}.`);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete service area');
    }
  };

  // ─── Region handlers ────────────────────────────────────────────────────────

  const openRegCreate = () => {
    setRegDraft({ ...EMPTY_REGION_DRAFT });
    setRegDialog({ open: true, mode: 'create', id: null });
  };

  const openRegEdit = (region: RegionAdminView) => {
    setRegDraft({ name: region.name, active: region.active });
    setRegDialog({ open: true, mode: 'edit', id: region.id });
  };

  const closeRegDialog = () => {
    setRegDialog({ open: false, mode: 'create', id: null });
    setSaving(false);
  };

  const handleRegSave = async () => {
    const name = regDraft.name.trim();
    if (!name) {
      setError('Region name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (regDialog.mode === 'create') {
        await serviceAreasAdminService.createRegion({ name, active: regDraft.active ?? true });
        setSuccess(`Created region "${name}".`);
      } else if (regDialog.id) {
        await serviceAreasAdminService.patchRegion(regDialog.id, { name, active: regDraft.active });
        setSuccess(`Updated region "${name}".`);
      }
      closeRegDialog();
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save region');
    } finally {
      setSaving(false);
    }
  };

  // ─── Assign dialog ──────────────────────────────────────────────────────────

  const openAssignDialog = (region: RegionAdminView) => {
    setSelectedZipIds([]);
    setAssignDialog({ open: true, regionId: region.id, regionName: region.name });
  };

  const closeAssignDialog = () => {
    setAssignDialog({ open: false, regionId: null, regionName: '' });
    setSelectedZipIds([]);
  };

  const handleAssign = async () => {
    if (!assignDialog.regionId || selectedZipIds.length === 0) {
      setError('Select at least one ZIP to assign.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await serviceAreasAdminService.assignServiceAreasToRegion(assignDialog.regionId, selectedZipIds);
      setSuccess(`Assigned ${selectedZipIds.length} ZIP(s) to "${assignDialog.regionName}".`);
      closeAssignDialog();
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign ZIPs');
    } finally {
      setSaving(false);
    }
  };

  const unassignedAreas = serviceAreas.filter((sa) => !sa.regionId);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Service Areas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage which ZIP codes are active for caregiver discovery (MVP-18.6) and group them
            into named regions for premium placement (MVP-18.25).
          </Typography>
        </Box>

        {error ? (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        ) : null}

        <Tabs value={tab} onChange={(_, v: number) => setTab(v)} aria-label="Service areas tabs">
          <Tab label="Service Areas" id="tab-service-areas" aria-controls="tabpanel-service-areas" />
          <Tab label="Regions" id="tab-regions" aria-controls="tabpanel-regions" />
        </Tabs>

        {/* ─── Service Areas Tab ───────────────────────────────────────── */}
        <Box
          role="tabpanel"
          id="tabpanel-service-areas"
          aria-labelledby="tab-service-areas"
          hidden={tab !== 0}
        >
          {tab === 0 && (
            <Stack spacing={2}>
              {/* Toolbar */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openSaCreate}
                  data-testid="service-areas-add"
                >
                  Add ZIP
                </Button>
                <Button
                  variant="text"
                  startIcon={<RefreshIcon />}
                  onClick={() => void loadAll()}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filterActive}
                    onChange={(e) => { setFilterActive(e.target.value as typeof filterActive); setSaPage(0); }}
                    data-testid="service-areas-filter-active"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active only</MenuItem>
                    <MenuItem value="inactive">Inactive only</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Region</InputLabel>
                  <Select
                    label="Region"
                    value={filterRegionId}
                    onChange={(e) => { setFilterRegionId(e.target.value); setSaPage(0); }}
                    data-testid="service-areas-filter-region"
                  >
                    <MenuItem value="">All regions</MenuItem>
                    <MenuItem value="__unassigned__">Unassigned</MenuItem>
                    {regions.map((r) => (
                      <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Paper variant="outlined">
                {loading ? (
                  <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small" aria-label="Service areas table">
                      <TableHead>
                        <TableRow>
                          <TableCell>ZIP</TableCell>
                          <TableCell>City</TableCell>
                          <TableCell>State</TableCell>
                          <TableCell>Region</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Active</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleAreas.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ py: 3, textAlign: 'center' }}
                              >
                                No service areas found. Add a ZIP code to get started.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          visibleAreas.map((sa) => (
                            <TableRow key={sa.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                  {sa.zip}
                                </Typography>
                              </TableCell>
                              <TableCell>{sa.city ?? '—'}</TableCell>
                              <TableCell>{sa.state ?? '—'}</TableCell>
                              <TableCell>
                                {sa.region ? (
                                  <Chip
                                    size="small"
                                    icon={<MapIcon fontSize="small" />}
                                    label={sa.region.name}
                                    variant="outlined"
                                    color="primary"
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    Unassigned
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={sa.active ? 'success' : 'default'}
                                  label={sa.active ? 'Active' : 'Inactive'}
                                />
                              </TableCell>
                              <TableCell>
                                <Switch
                                  size="small"
                                  checked={sa.active}
                                  onChange={() => void handleToggleActive(sa)}
                                  inputProps={{ 'aria-label': `Toggle active for ZIP ${sa.zip}` }}
                                  data-testid="service-area-active-toggle"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => openSaEdit(sa)}
                                    aria-label={`Edit ZIP ${sa.zip}`}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => void handleSaDelete(sa)}
                                    aria-label={`Delete ZIP ${sa.zip}`}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={filteredAreas.length}
                      page={saPage}
                      onPageChange={(_, newPage) => setSaPage(newPage)}
                      rowsPerPage={saRowsPerPage}
                      onRowsPerPageChange={(event) => {
                        setSaRowsPerPage(parseInt(event.target.value, 10));
                        setSaPage(0);
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                    />
                  </TableContainer>
                )}
              </Paper>
            </Stack>
          )}
        </Box>

        {/* ─── Regions Tab ─────────────────────────────────────────────── */}
        <Box
          role="tabpanel"
          id="tabpanel-regions"
          aria-labelledby="tab-regions"
          hidden={tab !== 1}
        >
          {tab === 1 && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openRegCreate}
                  data-testid="region-add"
                >
                  Add Region
                </Button>
                <Button
                  variant="text"
                  startIcon={<RefreshIcon />}
                  onClick={() => void loadAll()}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Stack>

              <Paper variant="outlined">
                {loading ? (
                  <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small" aria-label="Regions table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>ZIP Codes</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleRegions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ py: 3, textAlign: 'center' }}
                              >
                                No regions yet. Create one to group ZIP codes.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          visibleRegions.map((region) => (
                            <TableRow key={region.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {region.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={region.active ? 'success' : 'default'}
                                  label={region.active ? 'Active' : 'Inactive'}
                                />
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                  {region.serviceAreas.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                      None assigned
                                    </Typography>
                                  ) : (
                                    region.serviceAreas.slice(0, 8).map((sa) => (
                                      <Chip
                                        key={sa.id}
                                        size="small"
                                        label={sa.zip}
                                        variant="outlined"
                                        color={sa.active ? 'default' : 'error'}
                                      />
                                    ))
                                  )}
                                  {region.serviceAreas.length > 8 && (
                                    <Chip
                                      size="small"
                                      label={`+${region.serviceAreas.length - 8} more`}
                                      variant="outlined"
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Assign ZIPs">
                                  <IconButton
                                    size="small"
                                    onClick={() => openAssignDialog(region)}
                                    aria-label={`Assign ZIPs to region ${region.name}`}
                                    data-testid={`region-assign-${region.id}`}
                                  >
                                    <MapIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => openRegEdit(region)}
                                    aria-label={`Edit region ${region.name}`}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={regions.length}
                      page={regPage}
                      onPageChange={(_, newPage) => setRegPage(newPage)}
                      rowsPerPage={regRowsPerPage}
                      onRowsPerPageChange={(event) => {
                        setRegRowsPerPage(parseInt(event.target.value, 10));
                        setRegPage(0);
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                    />
                  </TableContainer>
                )}
              </Paper>
            </Stack>
          )}
        </Box>
      </Stack>

      {/* ─── Add / Edit Service Area Dialog ───────────────────────────── */}
      <Dialog open={saDialog.open} onClose={closeSaDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {saDialog.mode === 'create' ? 'Add service area (ZIP)' : `Edit ZIP ${saDraft.zip}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="ZIP code (5 digits)"
              value={saDraft.zip}
              onChange={(e) => setSaDraft((prev) => ({ ...prev, zip: e.target.value.trim() }))}
              disabled={saDialog.mode === 'edit'}
              inputProps={{ maxLength: 5, 'data-testid': 'service-area-zip' }}
              fullWidth
            />
            <TextField
              label="City"
              value={saDraft.city ?? ''}
              onChange={(e) => setSaDraft((prev) => ({ ...prev, city: e.target.value }))}
              inputProps={{ maxLength: 120, 'data-testid': 'service-area-city' }}
              fullWidth
            />
            <TextField
              label="State (2-letter code)"
              value={saDraft.state ?? ''}
              onChange={(e) =>
                setSaDraft((prev) => ({ ...prev, state: e.target.value.toUpperCase() }))
              }
              inputProps={{ maxLength: 2, 'data-testid': 'service-area-state' }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Region (optional)</InputLabel>
              <Select
                label="Region (optional)"
                value={saDraft.regionId ?? ''}
                onChange={(e) =>
                  setSaDraft((prev) => ({
                    ...prev,
                    regionId: e.target.value || null,
                  }))
                }
                inputProps={{ 'data-testid': 'service-area-region' }}
              >
                <MenuItem value="">None</MenuItem>
                {regions.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={saDraft.active ?? true}
                  onChange={(e) => setSaDraft((prev) => ({ ...prev, active: e.target.checked }))}
                  inputProps={{ 'aria-label': 'Service area active toggle' }}
                />
              }
              label={saDraft.active === false ? 'Inactive (hidden from discovery)' : 'Active'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSaDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSaSave()}
            disabled={saving}
            data-testid="service-areas-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Add / Edit Region Dialog ──────────────────────────────────── */}
      <Dialog open={regDialog.open} onClose={closeRegDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          {regDialog.mode === 'create' ? 'Create region' : `Edit region "${regDraft.name}"`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Region name"
              value={regDraft.name}
              onChange={(e) => setRegDraft((prev) => ({ ...prev, name: e.target.value }))}
              inputProps={{ maxLength: 120, 'data-testid': 'region-name' }}
              fullWidth
              autoFocus
            />
            <FormControlLabel
              control={
                <Switch
                  checked={regDraft.active ?? true}
                  onChange={(e) => setRegDraft((prev) => ({ ...prev, active: e.target.checked }))}
                  inputProps={{ 'aria-label': 'Region active toggle' }}
                />
              }
              label={regDraft.active === false ? 'Inactive' : 'Active'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRegDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleRegSave()}
            disabled={saving}
            data-testid="region-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Assign ZIPs to Region Dialog ─────────────────────────────── */}
      <Dialog open={assignDialog.open} onClose={closeAssignDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          Assign ZIPs to "{assignDialog.regionName}"
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Select unassigned ZIP codes to add to this region. Already-assigned ZIPs can be
              reassigned by editing individual service areas.
            </Typography>
            {unassignedAreas.length === 0 ? (
              <Alert severity="info">
                All active ZIP codes are already assigned to a region.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>ZIP codes</InputLabel>
                <Select
                  label="ZIP codes"
                  multiple
                  value={selectedZipIds}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedZipIds(typeof val === 'string' ? val.split(',') : val);
                  }}
                  inputProps={{ 'data-testid': 'region-assign-zips' }}
                  renderValue={(selected) =>
                    (selected as string[])
                      .map((id) => unassignedAreas.find((sa) => sa.id === id)?.zip ?? id)
                      .join(', ')
                  }
                >
                  {unassignedAreas.map((sa) => (
                    <MenuItem key={sa.id} value={sa.id}>
                      {sa.zip}{sa.city ? ` — ${sa.city}` : ''}{sa.state ? `, ${sa.state}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleAssign()}
            disabled={saving || selectedZipIds.length === 0}
            data-testid="region-assign-save"
          >
            {saving ? 'Assigning…' : `Assign ${selectedZipIds.length > 0 ? `(${selectedZipIds.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ServiceAreasPage;
